// NodeMissionTaskCountdown.jsx

import React, { useEffect, useMemo, useState } from 'react';

// Context
import { useTheme } from 'context/ThemeContext';

// Utilities
import { resolveMissionTaskTimeSpot } from 'missions/scheduling/missionScheduleUtils';

// CSS
import './NodeMissionTaskCountdown.css';

/**
 * NodeMissionTaskCountdown
 *
 * Header time spot: allotted before start, live timer while active, actual when complete.
 *
 * @param {object|null} missionRun - Parent workflow run.
 * @param {object|null} task - Workflow task.
 * @returns {ReactNode}
 */
const NodeMissionTaskCountdown = React.memo(({
  missionRun = null,
  task = null,
}) => {
  const { theme } = useTheme();
  const [nowMs, setNowMs] = useState(() => Date.now());

  const timeSpot = useMemo(() => (
    resolveMissionTaskTimeSpot({
      missionRun,
      nowMs,
      task,
    })
  ), [
    missionRun?.launchedAtMs,
    missionRun?.taskEvents,
    missionRun?.tasks,
    nowMs,
    task?.estimatedDurationDays,
    task?.estimatedDurationMinutes,
    task?.key,
    task?.readyAtMs,
    task?.status,
    task?.updatedAtMs,
  ]);

  useEffect(() => {
    if (!timeSpot?.live) return undefined;

    const intervalId = window.setInterval(() => {
      setNowMs(Date.now());
    }, 1000);

    return () => window.clearInterval(intervalId);
  }, [timeSpot?.live]);

  if (!timeSpot?.label) return null;

  const titleByMode = {
    actual: 'Actual time from ready to complete',
    allotted: 'Estimated duration for this step',
    timer: timeSpot.overdue
      ? 'Past estimated duration'
      : 'Time left on estimated duration',
  };

  return (
    <div
      className={[
        'node-mission-task-countdown',
        timeSpot.mode === 'timer' ? 'is-timer' : '',
        timeSpot.overdue ? 'is-overdue' : '',
      ].filter(Boolean).join(' ')}
      style={{
        //backgroundColor: theme.backgroundMuted,
        color: timeSpot.overdue ? theme.warning : theme.foregroundMuted,
      }}
      title={titleByMode[timeSpot.mode] || ''}
    >
      {timeSpot.label}
    </div>
  );
});

NodeMissionTaskCountdown.displayName = 'NodeMissionTaskCountdown';

export default NodeMissionTaskCountdown;
